export type SiteRegion = "auto" | "us" | "tr" | "eu" | "global";

export type EventLocationSelection = {
  countryCode: string | null;
  countryName: string | null;
  cityName: string | null;
  cityLabel: string | null;
  latitude: number | null;
  longitude: number | null;
};

export const REGION_OPTIONS: Array<{ value: SiteRegion; label: string }> = [
  { value: "auto", label: "AUTO" },
  { value: "us", label: "US" },
  { value: "tr", label: "TR" },
  { value: "eu", label: "EU" },
  { value: "global", label: "GLB" },
];

export const EMPTY_EVENT_LOCATION: EventLocationSelection = {
  countryCode: null,
  countryName: null,
  cityName: null,
  cityLabel: null,
  latitude: null,
  longitude: null,
};

export type EventLocationFilterTarget = {
  city: string | null;
  countryCode: string | null;
  latitude?: number | null;
  longitude?: number | null;
  market?: Exclude<SiteRegion, "auto">;
};

function normalizeLocationText(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

function normalizeCoordinate(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function haversineDistanceKm(
  leftLatitude: number,
  leftLongitude: number,
  rightLatitude: number,
  rightLongitude: number,
): number {
  const earthRadiusKm = 6371;
  const latitudeDelta = ((rightLatitude - leftLatitude) * Math.PI) / 180;
  const longitudeDelta = ((rightLongitude - leftLongitude) * Math.PI) / 180;
  const leftLatitudeRadians = (leftLatitude * Math.PI) / 180;
  const rightLatitudeRadians = (rightLatitude * Math.PI) / 180;

  const a =
    Math.sin(latitudeDelta / 2) * Math.sin(latitudeDelta / 2) +
    Math.sin(longitudeDelta / 2) * Math.sin(longitudeDelta / 2) * Math.cos(leftLatitudeRadians) * Math.cos(rightLatitudeRadians);
  return 2 * earthRadiusKm * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function formatEventLocationLabel(location: EventLocationSelection | null): string {
  if (!location) return "";
  const parts = [location.cityLabel ?? location.cityName, location.countryName].filter(Boolean);
  return parts.join(", ");
}

export function matchesEventLocation(
  event: EventLocationFilterTarget,
  location: EventLocationSelection | null,
  radiusKm = 200,
): boolean {
  if (!location) return true;

  const eventCountry = normalizeLocationText(event.countryCode);
  const selectedCountry = normalizeLocationText(location.countryCode);
  const eventCity = normalizeLocationText(event.city);
  const selectedCity = normalizeLocationText(location.cityName);
  const eventLatitude = normalizeCoordinate(event.latitude);
  const eventLongitude = normalizeCoordinate(event.longitude);
  const selectedLatitude = normalizeCoordinate(location.latitude);
  const selectedLongitude = normalizeCoordinate(location.longitude);

  if (
    eventLatitude !== null &&
    eventLongitude !== null &&
    selectedLatitude !== null &&
    selectedLongitude !== null
  ) {
    return haversineDistanceKm(selectedLatitude, selectedLongitude, eventLatitude, eventLongitude) <= Math.max(0, radiusKm);
  }

  if (selectedCountry && eventCountry && selectedCountry !== eventCountry) {
    return false;
  }

  if (selectedCity && eventCity && selectedCity !== eventCity) {
    return false;
  }

  if (selectedCity && !eventCity) {
    return false;
  }

  if (selectedCountry && !eventCountry) {
    return false;
  }

  return true;
}

export function filterEventsByLocation<T extends EventLocationFilterTarget>(
  events: T[],
  location: EventLocationSelection | null,
  radiusKm = 200,
): T[] {
  return events.filter((event) => matchesEventLocation(event, location, radiusKm));
}

export function defaultRegionForLanguage(language: "en" | "tr" | "de" | "uk"): Exclude<SiteRegion, "auto"> {
  void language;
  return "global";
}

export function resolveRegion(region: SiteRegion, language: "en" | "tr" | "de" | "uk"): Exclude<SiteRegion, "auto"> {
  return region === "auto" ? defaultRegionForLanguage(language) : region;
}
